import React, { memo, useState } from "react";

import { isEmpty, isEqual } from "lodash/fp";

import { useMediaQuery } from "@mui/material";

import { isFreePlan } from "@app/analytics/account/Billing/utils";
import MetricWithIndicator from "@app/analytics/components/MetricWithIndicator";
import { TableCell } from "@app/analytics/components/Table";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import ReportRowContextMenu from "@app/analytics/MeetingReports/components/ReportRowContextMenu";
import ReportActionsCell from "@app/analytics/MeetingReports/ReportsTable/cells/ReportActionsCell";
import { ReportTableRow } from "@app/analytics/MeetingReports/ReportsTable/styles";
import { isMenuCloseEvent } from "@app/analytics/MeetingReports/ReportsTable/utils";
import type { OnOpenReportHandler } from "@app/analytics/MeetingReports/types";
import theme from "@app/analytics/theme";
import type { UserMeeting } from "@app/common/types";
import * as SessionUtils from "@app/common/utils/sessionUtils";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";
import useContextMenu from "@app/utils/hooks/useContextMenu";

import { Column } from "@rui/Stack";

import DateAndTimeCell from "./DateAndTimeCell";
import MeetingReportProcessingRow from "./MeetingReportProcessingRow";
import MeetingReportRowActions, {
  ReportAction,
} from "./MeetingReportRowActions";
import MeetingReportTagPopover from "./MeetingReportTagPopover";
import MeetingReportTagsCell from "./MeetingReportTagsCell";
import MeetingTitleCell from "./MeetingTitleCell";
import { ExpandButton } from "./styles";
import OwnerCell from "../cells/OwnerCell";
import SourceCell from "../cells/SourceCell";

const EMPTY_MEETING_INSTANCES: UserMeeting[] = [];

interface MeetingReportTableRowProps {
  className?: string;
  report: UserMeeting;
  isNested?: boolean;
  isUnseen?: boolean;
  onOpenReport: OnOpenReportHandler;
  showAttendance?: boolean;
}

const MeetingReportTableRow = ({
  className,
  isNested = false,
  isUnseen = false,
  onOpenReport,
  report,
  showAttendance = false,
}: MeetingReportTableRowProps) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const [action, setAction] = useState<ReportAction | null>(null);

  const isDesktopView = useMediaQuery(theme.breakpoints.up("sm"));

  const { currentPlan } = useBillingContext();

  const {
    anchorEl: tagPopoverAnchorEl,
    open: tagEditPopoverOpen,
    onClose: onCloseTagPopover,
    onClick: onClickTags,
  } = useAnchorElement();

  const { handleContextMenuOpen, handleContextMenuClose, contextMenu } =
    useContextMenu();

  const isRestrictedReport = SessionUtils.isRestricted(report);

  // account for the period of time between when a user upgrades and when their
  // meetings are actually picked up for processing; will show processing state
  // if the meeting qualifies for processing on upgrade
  const isWaitingToBeProcessed =
    isRestrictedReport &&
    !isFreePlan(currentPlan.type) && // we only show the out-of-quota row if on a free plan
    report.isUpsellPreview &&
    !SessionUtils.isDataDeleted(report);

  if (SessionUtils.isProcessing(report) || isWaitingToBeProcessed) {
    return (
      <MeetingReportProcessingRow report={report} onOpenReport={onOpenReport} />
    );
  }

  const nestedRows = isNested
    ? EMPTY_MEETING_INSTANCES
    : report.instances ?? EMPTY_MEETING_INSTANCES;
  const isSummaryRow = !isNested && !isEmpty(nestedRows);

  const toggleExpanded = () => setExpanded((e) => !e);

  const handleClickExpandButton = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    toggleExpanded();
  };

  const handleClickRow = (
    e: React.MouseEvent<HTMLElement>,
    openNewTab = false
  ) => {
    if (isMenuCloseEvent(e)) return;
    if (isSummaryRow) return toggleExpanded();

    const newTab =
      openNewTab || e.button == 1 || e.buttons == 4 || e.ctrlKey || e.metaKey;
    onOpenReport(report.id, { newTab, type: "session" });
  };

  const canEditReport = report.permissions.includes("edit");
  const canDeleteReport = report.permissions.includes("delete");
  const canEditReportTitle = canEditReport && !isSummaryRow;

  const handleClickDeleteReport = () => {
    if (!canDeleteReport) return;
    setAction({ action: "delete", report });
  };

  const handleClickRemoveReport = () => {
    setAction({ action: "remove", report });
  };

  const handleClickUpgradeDetails = () => {
    setAction({ action: "upsell" });
  };

  const handleClickRenameReport = () => {
    setAction({ action: "rename", report });
  };

  const handleOpenNewTabClick = (e: React.MouseEvent<HTMLElement>) => {
    handleContextMenuClose();
    handleClickRow(e, true);
  };

  const handleTagClick = canEditReport ? onClickTags : undefined;
  return (
    <>
      <ReportTableRow
        data-test-id="meeting-report-table-row"
        data-sid={report.id}
        onClick={handleClickRow}
        onContextMenu={handleContextMenuOpen}
        expanded={expanded}
        onAuxClick={handleClickRow}
        nested={isNested}
        unseen={isUnseen}
        className={className}
      >
        {isSummaryRow ? (
          <TableCell>
            <Column variant="center">
              <ExpandButton
                expanded={expanded}
                onClick={handleClickExpandButton}
              />
            </Column>
          </TableCell>
        ) : (
          <SourceCell report={report} />
        )}
        <MeetingTitleCell
          report={report}
          isEditable={canEditReportTitle}
          onClickUpgradeDetails={handleClickUpgradeDetails}
          meetingTitleCellDenseContent={{
            isSummary: isSummaryRow,
            tags: report.tags,
            onTagClick: handleTagClick,
          }}
        />
        {isDesktopView && (
          <>
            <DateAndTimeCell
              report={report}
              isNested={isNested}
              isSummary={isSummaryRow}
              showAttendance={showAttendance && !isSummaryRow}
            />
            <TableCell sx={{ width: "120px" }}>
              <Column variant="center" flex={1}>
                <MetricWithIndicator value={report.meetingScore} />
              </Column>
            </TableCell>

            {isSummaryRow ? (
              <TableCell>
                {/* Tags cell placeholder. */}
                {/* TODO: would be cool to show any tags applied to the whole series */}
                {/* and support applying tags to a series using this cell */}
              </TableCell>
            ) : (
              <MeetingReportTagsCell
                tags={report.tags}
                onClick={handleTagClick}
              />
            )}
          </>
        )}

        <OwnerCell owner={report.ownerName} />
        {isSummaryRow ? (
          <TableCell>{/* Actions cell placeholder */}</TableCell>
        ) : (
          <ReportActionsCell
            report={report}
            canEditReport={canEditReport}
            onDeleteReport={
              canDeleteReport ? handleClickDeleteReport : undefined
            }
            onRemoveReport={handleClickRemoveReport}
            onRenameReport={handleClickRenameReport}
          />
        )}
      </ReportTableRow>
      {expanded &&
        nestedRows.map((nestedReport) => (
          <MeetingReportTableRow
            className="report-table-row--nested"
            key={nestedReport.id}
            report={nestedReport}
            onOpenReport={onOpenReport}
            isNested
          />
        ))}

      {/* These need to be rendered outside the row so that clicking on
          anything inside of them doesn't trigger the row's click handlers */}
      <MeetingReportRowActions
        action={action}
        onClose={() => setAction(null)}
      />
      <MeetingReportTagPopover
        report={report}
        popoverProps={{
          open: tagEditPopoverOpen,
          anchorEl: tagPopoverAnchorEl,
          onClose: onCloseTagPopover,
        }}
      />
      <ReportRowContextMenu
        handleContextMenuClose={handleContextMenuClose}
        contextMenuState={contextMenu}
        handleOpenNewTabClick={handleOpenNewTabClick}
      />
    </>
  );
};

const MemoizedMeetingReportTableRow = memo(MeetingReportTableRow, isEqual);

MemoizedMeetingReportTableRow.displayName = "MeetingReportTableRow";

export default MemoizedMeetingReportTableRow;
